import clsx from "clsx";
import AuthTitle from "../AuthTitle";
import LoginForm from "../LoginForm";
import ThirdPartyAuth from "../ThirdPartyAuth";
import useOrganization from "hooks/useOrganization";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement";
import Button from "components/Elements/Button/Button";
import getDomainNameFromURL from "../../helpers/getDomainNameFromURL";
import BelongToOrgErrorComponent from "./BelongToOrgErrorComponent";

import { memo, useEffect, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { CustomDomainPagesE } from "models/enums";

import styles from "./signInOrg.module.scss";

interface SignInOrgI extends React.HTMLAttributes<HTMLDivElement> {
  onSubmitSuccess?: (...args: unknown[]) => void;
  onSubUserSubmitSuccess?: (email: string) => void;
}

const isInvitePage =
  window.location.pathname.includes(CustomDomainPagesE.INVITE) ||
  window.location.pathname.includes(CustomDomainPagesE.SUB_USER_COMPLETE);

const SignInOrg = memo((props: SignInOrgI) => {
  const { className, onSubmitSuccess, onSubUserSubmitSuccess, ...restProps } =
    props;

  const { getTextByKey, getHtmlByKey } = useContent();

  const {
    orgAuthOptions,
    fetchOrgAuthOptions,
    checkIsLoginSourceButtonShown,
    loginSourceAuthOptions,
  } = useOrganization();

  const isChangeLoginOptionButtonShown = checkIsLoginSourceButtonShown();

  const [showTopUserAuthOptions, setShowTopUserAuthOptions] = useState(false);
  const [emailNotBelongToOrg, setEmailNotBelongToOrg] = useState("");

  const authOptions = showTopUserAuthOptions
    ? loginSourceAuthOptions
    : orgAuthOptions;

  useEffect(() => {
    fetchOrgAuthOptions();
  }, []);

  if (emailNotBelongToOrg) {
    return (
      <div className={clsx(styles.wrapper, className)} {...restProps}>
        <BelongToOrgErrorComponent
          email={emailNotBelongToOrg}
          setEmailNotBelongToOrg={setEmailNotBelongToOrg}
        />
      </div>
    );
  }

  return (
    <div className={clsx(styles.wrapper, className)} {...restProps}>
      <AuthTitle
        type={showTopUserAuthOptions ? "sign-in-as-root-user" : "sign-in"}
      />
      {!authOptions ? (
        <LoadingImageElement />
      ) : (
        <>
          {authOptions?.Email ? (
            <LoginForm
              submitTextKey="login"
              onSubmitSuccess={onSubmitSuccess}
              onSubUserSubmitSuccess={onSubUserSubmitSuccess}
              setEmailNotBelongToOrg={setEmailNotBelongToOrg}
            />
          ) : (
            <p className={styles.subUserJoinHint}>
              {getTextByKey("sub-user-join-hint").replace(
                "%domain%",
                getDomainNameFromURL()
              )}
            </p>
          )}
          <ThirdPartyAuth
            showOrDivider={orgAuthOptions?.Email}
            orgAuthOptions={authOptions}
            onSubmitSuccess={onSubmitSuccess}
            onSubUserSubmitSuccess={onSubUserSubmitSuccess}
            setEmailNotBelongToOrg={setEmailNotBelongToOrg}
          />
        </>
      )}
      <p className={styles.subText}>
        {getHtmlByKey("invite-text-subtitle")}
        <br />
        {getTextByKey("contact-administrator")}
      </p>
      {isChangeLoginOptionButtonShown && !isInvitePage ? (
        <Button
          onClick={() => setShowTopUserAuthOptions((prev) => !prev)}
          className={styles.changeSignInOptionButton}
        >
          {getTextByKey(
            showTopUserAuthOptions
              ? "sign-in-as-org-user"
              : "show-login-source-auth"
          )}
        </Button>
      ) : null}
    </div>
  );
});

export default SignInOrg;
